import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { detectLocaleFromGeo } from '../i18n/detectLocaleFromGeo';
import {
  DEFAULT_LOCALE,
  getLocaleFromPathname,
  getStoredLocalePref,
  setStoredLocalePref,
  stripLocalePrefix,
  withLocale,
  type AppLocale,
} from '../i18n/localeRouting';

/**
 * 1) URL locale → i18n 동기화
 * 2) 저장된 pref가 있으면 그 locale URL로 보정
 * 3) pref 없고 URL이 /en|/ja 이면 그 locale을 pref로 저장
 * 4) pref 없고 한국어 경로면 geo 감지 후 1회 리다이렉트
 */
export function LocaleBootstrap() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const runningRef = useRef(false);

  useEffect(() => {
    const pathLocale = getLocaleFromPathname(pathname);
    if (i18n.language !== pathLocale) {
      void i18n.changeLanguage(pathLocale);
    }
  }, [pathname, i18n]);

  useEffect(() => {
    if (runningRef.current) return;
    runningRef.current = true;

    let cancelled = false;

    const go = (locale: AppLocale) => {
      const bare = stripLocalePrefix(pathname);
      const next = `${withLocale(bare, locale)}${search}${hash}`;
      const current = `${pathname}${search}${hash}`;
      if (next !== current) {
        navigate(next, { replace: true });
      }
    };

    const pathLocale = getLocaleFromPathname(pathname);
    const stored = getStoredLocalePref();

    if (stored) {
      go(stored);
      return () => {
        cancelled = true;
      };
    }

    if (pathLocale !== DEFAULT_LOCALE) {
      setStoredLocalePref(pathLocale);
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      const detected = await detectLocaleFromGeo();
      if (cancelled) return;
      setStoredLocalePref(detected);
      go(detected);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
